import { useMatch } from '@tanstack/react-router';
import type { OnChangeFn, RowSelectionState } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { create } from 'zustand';

/**
 * Minimal row surface the selection toggle needs — structural so the handler
 * accepts an AppRow of any row type without generics.
 */
type SelectableRow = {
  id: string;
  getIsSelected: () => boolean;
};

type RouteSelectionState = {
  selections: { [routeKey: string]: string | null };
  setSelection: (routeKey: string, rowId: string | null) => void;
};

const useRouteSelectionStore = create<RouteSelectionState>((set) => ({
  selections: {},
  setSelection: (routeKey: string, rowId: string | null) =>
    set((state) => ({
      selections: { ...state.selections, [routeKey]: rowId },
    })),
}));

/**
 * Hook to manage TanStack Table row selection with route-based isolation.
 * Each route maintains its own selected row, persisted across navigation.
 *
 * @param keySuffix - Optional suffix to create separate selections within the same route
 *                    (e.g., 'buyup' vs 'regular' views on the same page)
 *
 * Returns props that can be spread directly to TanStackTable.
 */
export function useRouteRowSelection(keySuffix?: string) {
  // The page's own match, not the live location: the location changes the moment
  // a nav link is clicked, and re-rendering a whole table in that same render
  // holds back the tab highlight until it finishes. The match only moves when
  // the route commits, and by then this page is unmounting.
  const pathname = useMatch({ strict: false, select: (match) => match.pathname });
  const { selections, setSelection } = useRouteSelectionStore();

  // Build the selection key from pathname and optional suffix
  const selectionKey = keySuffix ? `${pathname}:${keySuffix}` : pathname;

  // Convert route-based selection to TanStack RowSelectionState
  const rowSelection: RowSelectionState = useMemo(() => {
    const selectedRowId = selections[selectionKey];
    if (selectedRowId) {
      return { [selectedRowId]: true };
    }

    return {};
  }, [selections, selectionKey]);

  // Handle selection changes from TanStack
  const onRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
    (updaterOrValue) => {
      const newSelection =
        typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : updaterOrValue;

      // Get the selected row ID (single selection)
      const selectedIds = Object.keys(newSelection).filter((id) => newSelection[id]);
      const selectedId = selectedIds.length > 0 ? selectedIds[0] : null;

      setSelection(selectionKey, selectedId);
    },
    [selectionKey, rowSelection, setSelection]
  );

  // Single-select toggle: clicking an already-selected row clears it.
  const onRowClick = useCallback(
    (row: SelectableRow) => {
      if (row.getIsSelected()) {
        setSelection(selectionKey, null);
      } else {
        setSelection(selectionKey, row.id);
      }
    },
    [selectionKey, setSelection]
  );

  return {
    rowSelection,
    onRowSelectionChange,
    onRowClick,
  };
}
